import { TransformComponent } from 'react-zoom-pan-pinch'
import { Button } from '@mui/material'
import {
  ArrowDropDown,
  ArrowDropUp,
  ArrowLeft,
  ArrowRight,
  ZoomIn,
  ZoomOut,
  CameraAlt,
  RotateRight,
  HelpOutline,
  Close,
  Block as BlockIcon,
  ChevronLeft,
  ChevronRight,
  Update,
  VisibilityOff,
  Visibility,
  ArrowBack,
} from '@mui/icons-material'
import Typography from '@mui/material/Typography'
import useTrayProcedureEditModeLogic from './TrayProcedureEditMode.logic'
import Tooltip from '@mui/material/Tooltip'

import { TrayProcedureEditModeProps } from './TrayProcedureEditMode.types'
import './TrayProcedureEditMode.scss'
import TrayMapSelector from '../TrayMapSelector/TrayMapSelector'
import ViewImageDialog from '../ViewImageDialog/ViewImageDialog'
import CancelChangesModal from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayVisualization/Modals/CancelChangesModal/CancelChangesModal'
import { ProcedureOtherItemsButton } from '../OtherTrayItems/OtherTrayItemsTriggerButton'
import ImageVerificationModal from './ImageVerificationModal/ImageVerificationModal'

const TrayProcedureEditMode = ({
  mapContent,
  trayType,
  onClickActions,
  isScrewEnabled,
  isScrewImplanted,
  isScrewWasted,
  handlePlateUsage,
  getPlateCount,
  setSelectedPlate,
  setModalOpen,
  setSelectedScrew,
  trayDetails,
  handleConfirm,
  setOtherTrayItemsModalOpen,
}: TrayProcedureEditModeProps) => {
  const {
    handlePan,
    zoomIn,
    zoomOut,
    handleZoomIn,
    handleZoomOut,
    imageScale,
    imageRotation,
    handleRotateImage,
    isImagePreviewOpen,
    setIsImagePreviewOpen,
    selectedScrewsCount,
    referenceImageTitle,
    hasNurseCapturedEndOfCaseReferenceImage,
    hasConfirmedHardware,
    showCancelModal,
    setShowCancelModal,
    handleCancel,
    handleMainButtonClick,
    shouldMainButtonBeDisabled,
    handleRecaptureReferenceImage,
    trayImage,
    isLegendOpen,
    setIsLegendOpen,
    screwsRemovedByNurse,
    formattedTimestamp,
    hasMultipleImages,
    isViewingHistory,
    goToPreviousImage,
    goToNextImage,
    goToLatestImage,
    currentImageIndex,
    totalImages,
    nurseUsageHistory,
    areControlsVisible,
    toggleControlsVisibility,
    isLatestImageByNurse,
    showImageVerificationModal,
    setShowImageVerificationModal,
    isImageLoading,
  } = useTrayProcedureEditModeLogic({
    handleConfirm,
    setModalOpen,
    trayDetails,
  })

  return (
    <div className="tray-procedure-edit-mode-organism_wrapper">
      <Button
        variant="text"
        className="back_button"
        sx={{ width: 'auto' }}
        size="small"
        onClick={() => setShowCancelModal(true)}
      >
        <ArrowBack sx={{ mr: 0.5 }} /> BACK
      </Button>

      <div className="content-container">
        {/* Reference Panel */}
        <div className="reference-panel">
          <div className="panel-header">
            <Typography variant="h6">{referenceImageTitle}</Typography>
            {formattedTimestamp && (
              <Typography variant="caption" className="timestamp">
                {formattedTimestamp}
              </Typography>
            )}
          </div>
          <div className="instruction-text">
            <Button
              className={`recapture-button ${
                !isLatestImageByNurse ||
                !hasNurseCapturedEndOfCaseReferenceImage
                  ? 'needs-attention'
                  : ''
              }`}
              onClick={handleRecaptureReferenceImage}
              startIcon={<CameraAlt />}
              size="small"
              variant="outlined"
            >
              {hasNurseCapturedEndOfCaseReferenceImage
                ? 'Re-capture'
                : 'Capture'}{' '}
              Image of Tray to Document Utilized Hardware
            </Button>
          </div>
          <div
            className={`reference-image-container ${
              !isLatestImageByNurse
                ? 'needs-recapture'
                : !hasNurseCapturedEndOfCaseReferenceImage
                ? 'needs-verification'
                : ''
            }`}
          >
            {isImageLoading ? (
              <div className="image-loading-container">
                <div className="loading-spinner" />
                <p>Loading image...</p>
              </div>
            ) : trayImage ? (
              <>
                <img
                  src={trayImage}
                  alt="Tray reference"
                  className="reference-image"
                  style={{
                    transform: `scale(${imageScale}) rotate(${imageRotation}deg)`,
                  }}
                />

                {hasMultipleImages && (
                  <div className="image-navigation-controls">
                    <button
                      onClick={goToPreviousImage}
                      disabled={currentImageIndex >= totalImages - 1}
                      className="nav-button prev-button"
                      aria-label="Previous image"
                    >
                      <ChevronLeft />
                    </button>

                    <div className="image-counter">
                      {totalImages - currentImageIndex} / {totalImages}
                    </div>

                    <button
                      onClick={goToNextImage}
                      disabled={currentImageIndex <= 0}
                      className="nav-button next-button"
                      aria-label="Next image"
                    >
                      <ChevronRight />
                    </button>
                  </div>
                )}

                {/* Only show "Return to Latest" if viewing history */}
                {isViewingHistory && hasMultipleImages && (
                  <button
                    onClick={goToLatestImage}
                    className="latest-image-button"
                    aria-label="Return to latest image"
                  >
                    <Update />
                    <span>Return to Latest</span>
                  </button>
                )}
              </>
            ) : (
              <div className="no-image-container">
                <p>No reference image available</p>
              </div>
            )}
          </div>
          <div className="reference-panel-controls">
            <button
              onClick={handleZoomOut}
              disabled={imageScale <= 0.5}
              aria-label="Zoom out"
            >
              <ZoomOut />
            </button>
            <button
              onClick={handleZoomIn}
              disabled={imageScale >= 3}
              aria-label="Zoom in"
            >
              <ZoomIn />
            </button>
            <button onClick={handleRotateImage} aria-label="Rotate image">
              <RotateRight />
            </button>
          </div>
        </div>

        {/* Map Container */}
        <div
          className={`map-container ${
            areControlsVisible ? 'controls-visible' : ''
          }`}
        >
          <div className="panel-header">
            <Typography variant="h6">Digital Tray Map</Typography>
            <div className="panel-header-controls">
              <button
                onClick={() => setIsLegendOpen(!isLegendOpen)}
                aria-label="Toggle Legend"
                title="Toggle Legend"
                className="legend-toggle-button"
              >
                <HelpOutline />
              </button>
              <button
                className={`controls-visibility-toggle ${
                  areControlsVisible ? 'controls-visible' : ''
                }`}
                onClick={toggleControlsVisibility}
                aria-label={
                  areControlsVisible
                    ? 'Hide navigation controls'
                    : 'Show navigation controls'
                }
                title={
                  areControlsVisible
                    ? 'Hide navigation controls'
                    : 'Show navigation controls'
                }
              >
                {areControlsVisible ? <VisibilityOff /> : <Visibility />}
                <span className="toggle-label">
                  {areControlsVisible ? 'Hide Controls' : 'Show Controls'}
                </span>
              </button>
            </div>
          </div>
          <div className="instruction-text">
            <div className="top-bar">
              <Typography variant="subtitle1">
                <span className="highlight-text">Select Utilized Hardware</span>
              </Typography>
              <div
                className="top-bar-actions"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <ProcedureOtherItemsButton
                  onClick={() => setOtherTrayItemsModalOpen(true)}
                  className="other-items-button"
                  trayType={trayType || ''}
                />
                <Tooltip
                  title={
                    selectedScrewsCount > 0
                      ? 'Click to confirm your hardware selection and move to the undocumented list'
                      : !isLatestImageByNurse
                      ? 'You need to capture a new reference image before proceeding'
                      : !hasNurseCapturedEndOfCaseReferenceImage
                      ? 'You need to verify the current reference image before proceeding'
                      : hasConfirmedHardware
                      ? "Click to complete this tray's hardware selection"
                      : 'Click to confirm no hardware was used from this tray'
                  }
                  placement="top"
                  arrow
                >
                  <span className="action-button-wrapper">
                    <Button
                      className={`toggle-button ${
                        shouldMainButtonBeDisabled
                          ? ''
                          : !isLatestImageByNurse
                          ? 'warning-action'
                          : !hasNurseCapturedEndOfCaseReferenceImage
                          ? 'verify-action'
                          : 'primary-action'
                      }`}
                      onClick={handleMainButtonClick}
                      disabled={shouldMainButtonBeDisabled}
                    >
                      {selectedScrewsCount > 0
                        ? `Confirm Selection (${selectedScrewsCount})`
                        : !isLatestImageByNurse
                        ? 'Capture Image Required'
                        : !hasNurseCapturedEndOfCaseReferenceImage
                        ? 'Verify Image & Continue'
                        : hasConfirmedHardware
                        ? 'Done'
                        : 'Done - No Hardware Selected'}
                    </Button>
                    {!shouldMainButtonBeDisabled && (
                      <div className="action-indicator" />
                    )}
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="tray-map_container">
            <TransformComponent
              contentClass="tray-map-content"
              wrapperClass="tray-map-wrapper"
            >
              <TrayMapSelector
                trayType={trayType}
                isInOR
                mapContent={mapContent}
                onClickActions={onClickActions}
                isScrewEnabled={isScrewEnabled}
                isScrewImplanted={isScrewImplanted}
                isScrewWasted={isScrewWasted}
                handlePlateUsage={handlePlateUsage}
                getPlateCount={getPlateCount}
                setSelectedPlate={setSelectedPlate}
                setModalOpen={setModalOpen}
                setSelectedScrew={setSelectedScrew}
                screwsRemovedByNurse={screwsRemovedByNurse}
                nurseUsageHistory={nurseUsageHistory}
              />
            </TransformComponent>
          </div>

          {/* Conditionally render controls based on visibility state */}
          {areControlsVisible && (
            <>
              <div className="controls-sidebar">
                <div className="zoom-controls">
                  <button
                    onClick={() => zoomOut(0.5)}
                    aria-label="Zoom Out (Ctrl+-)"
                    title="Zoom Out"
                  >
                    <ZoomOut />
                  </button>
                  <button
                    onClick={() => zoomIn(0.5)}
                    aria-label="Zoom In (Ctrl++)"
                    title="Zoom In"
                  >
                    <ZoomIn />
                  </button>
                </div>
                <div className="joystick-container">
                  <button
                    className="up-button"
                    onClick={() => handlePan('up')}
                    aria-label="Pan Up"
                  >
                    <ArrowDropUp />
                  </button>
                  <button
                    className="left-button"
                    onClick={() => handlePan('left')}
                    aria-label="Pan Left"
                  >
                    <ArrowLeft />
                  </button>
                  <button
                    className="right-button"
                    onClick={() => handlePan('right')}
                    aria-label="Pan Right"
                  >
                    <ArrowRight />
                  </button>
                  <button
                    className="down-button"
                    onClick={() => handlePan('down')}
                    aria-label="Pan Down"
                  >
                    <ArrowDropDown />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ViewImageDialog
        open={isImagePreviewOpen}
        handleClose={() => setIsImagePreviewOpen(false)}
        trayImage={trayImage || ''}
      />

      <CancelChangesModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        handleConfirm={handleCancel}
        handleSaveChangesCancellation={() => setShowCancelModal(false)}
        modalText={
          hasConfirmedHardware
            ? 'You have selected hardware used from this tray. If you go back these items will not be submitted to the procedure.'
            : 'You have not selected any hardware from this tray. Are you sure no hardware from this tray was used in the procedure?'
        }
      />

      <ImageVerificationModal
        open={showImageVerificationModal}
        onClose={() => setShowImageVerificationModal(false)}
        onConfirm={() => {
          setShowImageVerificationModal(false)
          handleConfirm()
        }}
        onRecapture={() => {
          setShowImageVerificationModal(false)
          handleRecaptureReferenceImage()
        }}
      />

      {/* Legend Overlay */}
      {isLegendOpen && (
        <div className="legend-overlay">
          <div className="legend-container">
            <div className="legend-header">
              <h3>Hardware Status Legend</h3>
              <button
                className="close-legend-button"
                onClick={() => setIsLegendOpen(false)}
                aria-label="Close Legend"
              >
                <Close />
              </button>
            </div>
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-circle blue" />
                <span>Loaded</span>
              </div>
              <div className="legend-item">
                <div className="legend-circle orange" />
                <span>Implanted</span>
              </div>
              <div className="legend-item">
                <div className="legend-circle wasted" />
                <span>Wasted</span>
              </div>
              <div className="legend-item">
                <div className="legend-circle not-loaded">
                  <BlockIcon className="disabled-icon" />
                </div>
                <span>Not Loaded from SPD</span>
              </div>
              <div className="legend-item">
                <div className="legend-circle removed-by-nurse">
                  <BlockIcon className="disabled-icon" />
                </div>
                <span>Not Present Per RN</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TrayProcedureEditMode
